import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  useColorModeValue,
  Image,
  FormErrorMessage,
  InputGroup,
  InputRightElement,
  Icon,
} from "@chakra-ui/react";
import DefaultAuth from "layouts/auth/Default";
import illustration from "assets/img/auth/auth.png";
import { NavLink } from "react-router-dom";
import { useAuth } from 'hooks/useAuth';
import { useToastNotification } from 'utils/toast-utils';
import { useFormik } from 'formik';
import { registerSchema } from '../schemas';
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { ReactComponent as Logo } from "assets/img/layout/Logor.svg";

function Register() {
  const { showToast } = useToastNotification();
  const { register, error, loading } = useAuth();
  
  const [show, setShow] = React.useState({
    password: false,
    confirmPassword: false
  });

  const handleShowPassword = (field) => {
    setShow(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const formik = useFormik({
    initialValues: {
      companyName: '',
      rif: '',
      contactName: '',
      email: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
      termsAccepted: false,
    },
    validationSchema: registerSchema,
    onSubmit: async (values) => {
      try {
        const response = await register(values);
        if (response) {
          showToast(
            'Success',
            'Registration successful! Please login.',
            'success'
          );
        }
      } catch (err) {
        showToast(
          'Error',
          err.response?.data?.message || 'Registration failed',
          'error'
        );
        throw err;
      }
    },
  });

  const textColor = useColorModeValue("#42505C", "white");
  const textColorSecondary = "#404040";
  const brandColor = "#6B46C1";
  const brandStars = useColorModeValue("#42505C", "brand.400");
  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w="100%"
        mx={{ base: "auto", lg: "-120px" }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        flexDirection="column"
      >
        <Box mb="24px">
          <Logo />
        </Box>
        <Flex
          direction={{ base: "column", md: "row" }}
          gap="24px"
          w="100%"
          mb="36px"
          maxW={{ base: "100%", md: "50%" }}
        >
          <Box flex="1">
            <Text
              fontSize="36px"
              color={textColor}
              fontWeight="700"
              lineHeight="1.5"
              mb="10px"
            >
              Input your information
            </Text>
          </Box>
          <Box flex="1">
            <Text
              color={textColorSecondary}
              fontWeight="400"
              fontSize="14px"
              lineHeight="2"
              mt="10px"
            >
              We need you to help us with some basic information for your
              account creation. Here are our{" "}
              <NavLink to="#">
                <Text as="span" color={brandColor} fontWeight="500">
                  terms and conditions
                </Text>
              </NavLink>
              . Please read them carefully. We are GDPR compliant
            </Text>
          </Box>
        </Flex>

        <Flex
          zIndex="2"
          direction="column"
          w="100%"
          maxW={{ base: "100%", md: "50%" }}
          background="transparent"
          borderRadius="15px"
          mx={{ base: "auto", lg: "unset" }}
          me="auto"
          mb={{ base: "20px", md: "auto" }}
        >
          <FormControl as="form" onSubmit={formik.handleSubmit}>
            <Flex
              direction={{ base: "column", md: "row" }}
              gap="24px"
              mb="24px"
            >
              <Box flex="1">
                <FormControl 
                  isInvalid={formik.touched.companyName && formik.errors.companyName}
                  isRequired
                >
                  <FormLabel
                    display="flex"
                    ms="4px"
                    fontSize="sm"
                    fontWeight="500"
                    color={textColor}
                    mb="8px"
                  >
                    Company Name
                  </FormLabel>
                  <Input
                    variant="auth"
                    fontSize="sm"
                    type="text"
                    placeholder="Company Name"
                    size="lg"
                    width="300px"
                    height="48px"
                    borderRadius="6px"
                    border="1px"
                    name="companyName"
                    value={formik.values.companyName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <FormErrorMessage>{formik.errors.companyName}</FormErrorMessage>
                </FormControl>
              </Box>
              <Box flex="1">
                <FormControl 
                  isInvalid={formik.touched.rif && formik.errors.rif}
                  isRequired
                >
                  <FormLabel
                    display="flex"
                    ms="4px"
                    fontSize="sm"
                    fontWeight="500"
                    color={textColor}
                    mb="8px"
                  >
                    RIF (Tax Identification Number)
                  </FormLabel>
                  <Input
                    variant="auth"
                    fontSize="sm"
                    type="text"
                    placeholder="J-123456789"
                    size="lg"
                    width="300px"
                    height="48px"
                    borderRadius="6px"
                    border="1px"
                    name="rif"
                    value={formik.values.rif}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <FormErrorMessage>{formik.errors.rif}</FormErrorMessage>
                </FormControl>
              </Box>
            </Flex>

            <Flex
              direction={{ base: "column", md: "row" }}
              gap="24px"
              mb="24px"
            >
              <Box flex="1">
                <FormControl 
                  isInvalid={formik.touched.contactName && formik.errors.contactName}
                  isRequired
                >
                  <FormLabel
                    display="flex"
                    ms="4px"
                    fontSize="sm"
                    fontWeight="500"
                    color={textColor}
                    mb="8px"
                  >
                    Contact Name
                  </FormLabel>
                  <Input
                    variant="auth"
                    fontSize="sm"
                    type="text"
                    placeholder="John Doe"
                    size="lg"
                    width="300px"
                    height="48px"
                    borderRadius="6px"
                    border="1px"
                    name="contactName"
                    value={formik.values.contactName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <FormErrorMessage>{formik.errors.contactName}</FormErrorMessage>
                </FormControl>
              </Box>
              <Box flex="1">
                <FormControl 
                  isInvalid={formik.touched.email && formik.errors.email}
                  isRequired
                >
                  <FormLabel
                    display="flex"
                    ms="4px"
                    fontSize="sm"
                    fontWeight="500"
                    color={textColor}
                    mb="8px"
                  >
                    Email
                  </FormLabel>
                  <Input
                    variant="auth"
                    fontSize="sm"
                    type="email"
                    placeholder="john.doe@example.com"
                    size="lg"
                    width="300px"
                    height="48px"
                    borderRadius="6px"
                    border="1px"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
                </FormControl>
              </Box>
            </Flex>

            <FormControl 
              isInvalid={formik.touched.phoneNumber && formik.errors.phoneNumber}
              isRequired
              mb="24px"
            >
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                mb="8px"
              >
                Phone Number
              </FormLabel>
              <Input
                variant="auth"
                fontSize="sm"
                type="tel"
                placeholder="+12123456789"
                size="lg"
                width="300px"
                height="48px"
                borderRadius="6px"
                border="1px"
                name="phoneNumber"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <FormErrorMessage>{formik.errors.phoneNumber}</FormErrorMessage>
            </FormControl>

            <Flex
              direction={{ base: "column", md: "row" }}
              gap="24px"
              mb="24px"
            >
              <Box flex="1">
                <FormControl 
                  isInvalid={formik.touched.password && formik.errors.password}
                  isRequired
                >
                  <FormLabel
                    ms="4px"
                    fontSize="sm"
                    fontWeight="500"
                    color={textColor}
                    display="flex"
                  >
                    Password
                  </FormLabel>
                  <InputGroup>
                    <Input
                      variant="auth"
                      fontSize="sm"
                      type={show.password ? "text" : "password"}
                      placeholder="Min. 8 characters"
                      size="lg"
                      width="300px"
                      height="48px"
                      borderRadius="6px"
                      border="1px"
                      name="password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <InputRightElement display='flex' alignItems='center' mt='4px'>
                      <Icon
                        color={textColorSecondary}
                        _hover={{ cursor: "pointer" }}
                        as={show.password ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                        onClick={() => handleShowPassword('password')}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{formik.errors.password}</FormErrorMessage>
                </FormControl>
              </Box>
              <Box flex="1">
                <FormControl 
                  isInvalid={formik.touched.confirmPassword && formik.errors.confirmPassword}
                  isRequired
                >
                  <FormLabel
                    ms="4px"
                    fontSize="sm"
                    fontWeight="500"
                    color={textColor}
                    display="flex"
                  >
                    Confirm Password
                  </FormLabel>
                  <InputGroup>
                    <Input
                      variant="auth"
                      fontSize="sm"
                      type={show.confirmPassword ? "text" : "password"}
                      placeholder="Min. 8 characters"
                      size="lg"
                      width="300px"
                      height="48px"
                      borderRadius="6px"
                      border="1px"
                      name="confirmPassword"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <InputRightElement display='flex' alignItems='center' mt='4px'>
                      <Icon
                        color={textColorSecondary}
                        _hover={{ cursor: "pointer" }}
                        as={show.confirmPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                        onClick={() => handleShowPassword('confirmPassword')}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{formik.errors.confirmPassword}</FormErrorMessage>
                </FormControl>
              </Box>
            </Flex>
          <Flex gap="10px" alignItems="center" justifyContent="space-between" w="100%" mt={7}>
            <FormControl 
              isInvalid={formik.touched.termsAccepted && formik.errors.termsAccepted}
              mb="24px"
            >
              <Flex align="center">
                <Checkbox 
                  colorScheme="purple" 
                  me="10px" 
                  name="termsAccepted"
                  isChecked={formik.values.termsAccepted}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <Text color={textColorSecondary} fontSize="sm">
                  I agree with the{" "}
                  <NavLink to="#">
                    <Text as="span" color={brandColor} fontWeight="500">
                      terms and conditions
                    </Text>
                  </NavLink>
                </Text>
              </Flex>
              <FormErrorMessage>{formik.errors.termsAccepted}</FormErrorMessage>
            </FormControl>
            <Button
              fontSize="sm"
              variant="brand"
              fontWeight="500"
              h="50"
              mb="24px"
              colorScheme="purple"
              color="white"
              p="10px 30px"
              _hover={{ bg: "#5835A8" }}
              onClick={formik.handleSubmit}
              isLoading={loading}
              isDisabled={!formik.isValid}
              type="submit"
            >
              Register
            </Button>
          </Flex>
      



            <Flex justify="center" align="center" mt="10px">
              <Text color={textColorSecondary} fontSize="sm" me="5px">
                Already have an account?
              </Text>
              <NavLink to="/auth/login">
                <Text color={brandColor} fontSize="sm" fontWeight="500">
                  Sign In
                </Text>
              </NavLink>
            </Flex>
          </FormControl>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default Register;
